import React from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Play, ExternalLink } from "lucide-react";
import useScrollAnimation from "../hooks/useScrollAnimation";

const AccessSection = () => {
  const [isVisible, sectionRef] = useScrollAnimation(0.2);
  const ceos = [
    {
      name: "Tom Palmer",
      title: "CEO of Newmont",
      image: "https://customer-assets.emergentagent.com/job_xd-project-landing/artifacts/1ak46kyj_Palmer-370x198.jpg",
      youtubeUrl: "https://www.youtube.com/watch?v=CoVFRTLdXEo"
    },
    {
      name: "Mark Bristow", 
      title: "CEO of Barrick Gold",
      image: "https://customer-assets.emergentagent.com/job_xd-project-landing/artifacts/kvubqr1t_Bristow-370x198.jpg",
      youtubeUrl: "https://www.youtube.com/watch?v=dBlWMPszK8Y"
    },
    {
      name: "Frank Giustra",
      title: "President and CEO, Fiore Group",
      image: "https://customer-assets.emergentagent.com/job_xd-project-landing/artifacts/v4kslh67_Giustra-370x198.jpg",
      youtubeUrl: "https://www.youtube.com/watch?v=rivswbFfTwA"
    },
    {
      name: "Maximo Pacheco",
      title: "Chair, Codelco",
      image: "https://customer-assets.emergentagent.com/job_xd-project-landing/artifacts/9canyg98_Maximo-370x198.jpg",
      youtubeUrl: "https://www.youtube.com/watch?v=NOZuh8pqugQ"
    },
    {
      name: "Eric Sprott",
      title: "Chief Executive Officer, Eric Sprott Family Office",
      image: "https://customer-assets.emergentagent.com/job_xd-project-landing/artifacts/sh4si2nk_Sprott-370x198.jpg",
      youtubeUrl: "https://www.youtube.com/watch?v=yl7aQlozL9M"
    }
  ];

  return (
    <section 
      ref={sectionRef}
      className={`py-20 bg-gray-900 transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-yellow-500 mb-4">
            Access
          </h2>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            We interview the biggest names in mining. Collectively, the CEOs we have interviewed are responsible for billions of dollars in assets and have signed off on hundreds of millions in purchases.
          </p>
        </div>

        {/* CEO Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {ceos.map((ceo, index) => (
            <Card 
              key={index} 
              className="bg-gray-800 border-yellow-500/20 hover:border-yellow-500/80 hover:shadow-[0_0_30px_rgba(244,185,66,0.4)] transition-all duration-500 group overflow-hidden hover:scale-105 cursor-pointer relative before:absolute before:inset-0 before:bg-gradient-to-t before:from-yellow-500/20 before:to-transparent before:opacity-0 hover:before:opacity-100 before:transition-opacity before:duration-500 hover:-translate-y-2"
              style={{
                animation: isVisible ? `fadeInUp 0.6s ease-out ${index * 0.1}s forwards` : 'none',
                opacity: isVisible ? 1 : 0
              }}
            >
              <CardContent className="p-0 relative">
                <div className="aspect-[370/198] overflow-hidden relative">
                  <img 
                    src={ceo.image}
                    alt={ceo.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <Button 
                      size="sm" 
                      className="bg-yellow-500 hover:bg-yellow-400 text-black font-semibold transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const url = ceo.youtubeUrl;
                        console.log('Opening URL:', url); // Debug log
                        window.open(url, '_blank', 'noopener,noreferrer');
                      }}
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Watch Interview
                    </Button>
                  </div>
                  {/* Corner Badge */}
                  <div className="absolute top-2 right-2 bg-yellow-500/90 text-black text-xs font-bold px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    CEO SPOTLIGHT
                  </div>
                </div>
                <div className="p-4 text-center bg-gradient-to-t from-gray-800 to-gray-800/95">
                  <h3 className="text-white font-bold text-lg mb-1 group-hover:text-yellow-500 transition-colors">
                    {ceo.name}
                  </h3>
                  <p className="text-gray-400 text-sm group-hover:text-gray-300 transition-colors">
                    {ceo.title}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Additional Stats */}
        <div className="mt-16 text-center">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="text-3xl font-bold text-yellow-500 mb-2">200+</div>
              <div className="text-white font-semibold">CEO Interviews</div>
              <div className="text-gray-400 text-sm">Industry leaders featured</div>
            </div>
            
            <div>
              <div className="text-3xl font-bold text-yellow-500 mb-2">$500B+</div>
              <div className="text-white font-semibold">Assets Under Management</div>
              <div className="text-gray-400 text-sm">Collective responsibility</div>
            </div>
            
            <div>
              <div className="text-3xl font-bold text-yellow-500 mb-2">50+</div>
              <div className="text-white font-semibold">Countries Represented</div>
              <div className="text-gray-400 text-sm">Global mining reach</div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};

export default AccessSection;